import {
  DistrictData,
  ReviewAction,
  ReviewResult,
  ReviewSession,
} from "@/types/review";

const STORAGE_KEY = "url-review-session";

export function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function createNewSession(totalDistricts: number): ReviewSession {
  return {
    session_id: generateSessionId(),
    start_time: new Date().toISOString(),
    districts_reviewed: 0,
    current_index: 0,
    total_districts: totalDistricts,
    reviews: [],
  };
}

export function saveSession(session: ReviewSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    console.error("Failed to save session to localStorage:", error);
  }
}

export function loadSession(): ReviewSession | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved) as ReviewSession;
    }
  } catch (error) {
    console.error("Failed to load session from localStorage:", error);
  }
  return null;
}

export function clearSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear session from localStorage:", error);
  }
}

export function createReviewResult(
  district: DistrictData,
  action: ReviewAction,
  notes: string,
  sessionId: string,
  reviewedUrl: string,
): ReviewResult {
  return {
    nces_id: district.nces_id,
    district_name: district.district_name,
    review_status: action === "skip" ? "skipped" : action,
    reviewer_notes: notes,
    review_timestamp: new Date().toISOString(),
    session_id: sessionId,
    reviewed_url: reviewedUrl,
  };
}

export function updateSessionWithReview(
  session: ReviewSession,
  review: ReviewResult,
  moveToNext: boolean = true,
): ReviewSession {
  const updatedReviews = [...session.reviews];
  const existingIndex = updatedReviews.findIndex(
    (r) => r.nces_id === review.nces_id,
  );

  if (existingIndex >= 0) {
    updatedReviews[existingIndex] = review;
  } else {
    updatedReviews.push(review);
  }

  const reviewedCount = updatedReviews.filter(
    (r) => r.review_status !== "unreviewed",
  ).length;

  return {
    ...session,
    reviews: updatedReviews,
    districts_reviewed: reviewedCount,
    current_index: moveToNext
      ? Math.min(session.current_index + 1, session.total_districts - 1)
      : session.current_index,
  };
}

export function getSessionStats(session: ReviewSession) {
  const totalReviewed = session.reviews.filter(
    (r) => r.review_status !== "unreviewed",
  ).length;
  const correctCount = session.reviews.filter(
    (r) => r.review_status === "correct",
  ).length;
  const incorrectCount = session.reviews.filter(
    (r) => r.review_status === "incorrect",
  ).length;
  const skippedCount = session.reviews.filter(
    (r) => r.review_status === "skipped",
  ).length;

  const startTime = new Date(session.start_time);
  const currentTime = new Date();
  const timeElapsed = Math.floor(
    (currentTime.getTime() - startTime.getTime()) / 1000 / 60,
  ); // minutes

  const reviewsPerHour =
    timeElapsed > 0 ? Math.round((totalReviewed / timeElapsed) * 60) : 0;
  const completionPercentage = Math.round(
    (totalReviewed / session.total_districts) * 100,
  );

  return {
    totalReviewed,
    correctCount,
    incorrectCount,
    skippedCount,
    timeElapsed,
    reviewsPerHour,
    completionPercentage,
    remaining: session.total_districts - totalReviewed,
  };
}
